/**
 * Shared date utilities for YYYY-MM month-level operations.
 */

export interface DateOnlyParts {
  year: number;
  month: number;
  day: number;
}

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse a YYYY-MM-DD string into validated calendar date parts.
 */
export function parseDateOnlyParts(value: string): DateOnlyParts | null {
  const match = DATE_ONLY_PATTERN.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (year < 1 || year > 9999) return null;

  // Use setFullYear to avoid the JS 0-99 year remapping behavior.
  const date = new Date(0);
  date.setFullYear(year, month - 1, day);
  date.setHours(0, 0, 0, 0);

  if (
    date.getFullYear() !== year ||
    date.getMonth() + 1 !== month ||
    date.getDate() !== day
  ) {
    return null;
  }

  return { year, month, day };
}

/**
 * Validate that a value is a YYYY-MM-DD calendar date string.
 */
export function isDateOnlyString(value: unknown): value is string {
  return typeof value === "string" && parseDateOnlyParts(value) !== null;
}

/**
 * Parse a YYYY-MM-DD string into a local-midnight Date for UI/date-picker use.
 */
export function parseDateOnlyLocal(value: string): Date | null {
  const parts = parseDateOnlyParts(value);
  if (!parts) return null;

  const date = new Date(0);
  date.setFullYear(parts.year, parts.month - 1, parts.day);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * Format a Date into YYYY-MM-DD using local calendar fields.
 */
export function formatDateOnlyLocal(date: Date): string {
  if (Number.isNaN(date.getTime())) {
    throw new Error("Cannot format an invalid Date");
  }

  const year = date.getFullYear();
  if (year < 1 || year > 9999) {
    throw new Error(
      `Year ${year} is outside the supported range (1-9999) for date-only formatting`,
    );
  }

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${String(year).padStart(4, "0")}-${month}-${day}`;
}

/**
 * Get the last day of a given YYYY-MM month string as a YYYY-MM-DD string.
 *
 * @example getMonthEndDate("2026-02") → "2026-02-28"
 */
export function getMonthEndDate(yearMonth: string): string {
  const parts = yearMonth.split("-");
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const lastDay = new Date(year, month, 0).getDate();
  return `${yearMonth}-${String(lastDay).padStart(2, "0")}`;
}

/**
 * Get the last day of a month given a year and 1-based month number.
 *
 * @example getLastDayOfMonth(2026, 2) → 28
 */
export function getLastDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/**
 * Build list of YYYY-MM strings from January of the given year
 * through the last fully completed month (relative to today).
 *
 * Returns an empty array if the year is in the future.
 * For past years, returns all 12 months.
 * For the current year, returns Jan through the previous month.
 *
 * @example (in March 2026) getCompletedMonths(2026) → ["2026-01", "2026-02"]
 * @example getCompletedMonths(2025) → ["2025-01", ..., "2025-12"]
 */
export function getCompletedMonths(year: number): string[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-based

  if (year > currentYear) return [];

  const lastMonth = year < currentYear ? 12 : currentMonth - 1;
  const months: string[] = [];
  for (let m = 1; m <= lastMonth; m++) {
    months.push(`${year}-${String(m).padStart(2, "0")}`);
  }
  return months;
}
